// FILE: src/pages/ZReportExpectedPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import API from "../api";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";

// Helper: generate a default expected line with new fields
function createDefaultLine() {
  const today = new Date().toISOString().split("T")[0];
  return {
    expectedDate: today,
    category: "",
    targetCash: "",
    declaredCash: "",
    tillVariance: 0,
    expectedAmount: 0,
    user: "",
    comments: ""
  };
}

function ZReportExpectedPage() {
  const navigate = useNavigate();

  // Selectable data
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);

  // Lines in our form
  const [lines, setLines] = useState([createDefaultLine()]);

  useEffect(() => {
    loadAccounts();
    loadCategories();
    loadUsers();
  }, []);

  async function loadAccounts() {
    try {
      const res = await API.get("/accounts?limit=100000");
      setAccounts(res.data.accounts || []);
    } catch (err) {
      console.error("Failed to load accounts:", err);
    }
  }

  async function loadCategories() {
    try {
      const res = await API.get("/categories?limit=100000");
      // Filter categories as needed (e.g., type === "C2B")
      const c2bCategories = res.data.filter((cat) => cat.type === "C2B");
      setCategories(c2bCategories);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  }

  async function loadUsers() {
    try {
      const res = await API.get("/users?limit=1000000");
      setUsers(res.data || []);
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Line Management Functions
  // ──────────────────────────────────────────────────────────────
  function handleAddLine() {
    setLines((prev) => [...prev, createDefaultLine()]);
  }

  function handleAddFiveLines() {
    const newLines = Array.from({ length: 5 }, () => createDefaultLine());
    setLines((prev) => [...prev, ...newLines]);
  }

  function handleDuplicateLine(idx) {
    const newLine = { ...lines[idx] };
    setLines((prev) => [...prev, newLine]);
  }

  function handleRemoveLine(idx) {
    const newLines = [...lines];
    newLines.splice(idx, 1);
    setLines(newLines);
  }

  function handleLineChange(idx, field, value) {
    const newLines = [...lines];
    newLines[idx][field] = value;

    // Recompute auto fields if targetCash or declaredCash changes
    if (field === "targetCash" || field === "declaredCash") {
      const target = parseFloat(newLines[idx].targetCash) || 0;
      const declared = parseFloat(newLines[idx].declaredCash) || 0;
      newLines[idx].tillVariance = target - declared;
      newLines[idx].expectedAmount = declared - 150;
    }
    setLines(newLines);
  }

  // ──────────────────────────────────────────────────────────────
  // Saving Function
  // ──────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!accountId) {
      alert("Please select an account first.");
      return;
    }
    if (lines.length === 0) {
      alert("No lines to save.");
      return;
    }

    const payload = {
      accountId,
      lines: lines.map((ln) => ({
        expectedDate: ln.expectedDate,
        category: ln.category,
        targetCash: Number(ln.targetCash),
        declaredCash: Number(ln.declaredCash),
        tillVariance: Number(ln.targetCash) - Number(ln.declaredCash),
        expectedAmount: Number(ln.declaredCash) - 150,
        user: ln.user,
        comments: ln.comments
      }))
    };

    try {
      const res = await API.post("/zreports/expected/create", payload);
      alert(res.data.message);
      navigate("/zreport-list");
    } catch (err) {
      console.error("ZReportExpectedPage Error:", err);
      alert("Failed to create expected lines.");
    }
  }

  // ──────────────────────────────────────────────────────────────
  // Prepare account options (sorted A–Z)
  const accountOptions = useMemo(() => {
    return accounts.slice().sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
  }, [accounts]);

  return (
    // maxWidth={false} => full browser width
    // disableGutters => no default side padding
    <Container maxWidth={false} disableGutters sx={{ px: 2, py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        Add Z Report Expected Lines
      </Typography>

      {/* Account Selection */}
      <Box sx={{ mb: 3, maxWidth: 1200, mx: "auto" }}>
        <Autocomplete
          options={accountOptions}
          getOptionLabel={(option) =>
            `${option.accountName} (${option.accountNo})`
          }
          onChange={(event, newValue) =>
            setAccountId(newValue ? newValue._id : "")
          }
          value={accountOptions.find((acc) => acc._id === accountId) || null}
          renderInput={(params) => (
            <TextField {...params} label="Account" variant="outlined" fullWidth />
          )}
          ListboxProps={{ style: { maxHeight: 200 } }}
        />
      </Box>

      {/* For each line: single row, no wrapping */}
      {lines.map((line, idx) => (
        <Card
          key={idx}
          variant="outlined"
          sx={{
            mb: 2,
            maxWidth: 1800, // or any wide container
            mx: "auto",
            overflowX: "auto"
          }}
        >
          <CardContent>
            {/* Grid container with wrap="nowrap" => horizontal scroll if needed */}
            <Grid
              container
              spacing={2}
              alignItems="center"
              wrap="nowrap"
              sx={{ minWidth: 1200 }} // ensure wide enough for all fields in one row
            >
              {/* Expected Date */}
              <Grid item sx={{ minWidth: 120 }}>
                <TextField
                  label="Date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={line.expectedDate}
                  onChange={(e) =>
                    handleLineChange(idx, "expectedDate", e.target.value)
                  }
                />
              </Grid>

              {/* Category */}
              <Grid item sx={{ minWidth: 160 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={line.category}
                    onChange={(e) =>
                      handleLineChange(idx, "category", e.target.value)
                    }
                  >
                    <MenuItem value="">
                      <em>-- Select --</em>
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat._id} value={cat._id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Target Cash */}
              <Grid item sx={{ minWidth: 120 }}>
                <TextField
                  label="Target Cash"
                  type="number"
                  required
                  fullWidth
                  value={line.targetCash}
                  onChange={(e) =>
                    handleLineChange(idx, "targetCash", e.target.value)
                  }
                />
              </Grid>

              {/* Declared Cash */}
              <Grid item sx={{ minWidth: 120 }}>
                <TextField
                  label="Declared Cash"
                  type="number"
                  required
                  fullWidth
                  value={line.declaredCash}
                  onChange={(e) =>
                    handleLineChange(idx, "declaredCash", e.target.value)
                  }
                />
              </Grid>

              {/* Till Variance */}
              <Grid item sx={{ minWidth: 120 }}>
                <TextField
                  label="Till Variance"
                  type="number"
                  fullWidth
                  value={line.tillVariance}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* Expected Amount */}
              <Grid item sx={{ minWidth: 120 }}>
                <TextField
                  label="Expected Amt"
                  type="number"
                  fullWidth
                  value={line.expectedAmount}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* User */}
              <Grid item sx={{ minWidth: 160 }}>
                <FormControl fullWidth>
                  <InputLabel>User (opt.)</InputLabel>
                  <Select
                    label="User (opt.)"
                    value={line.user}
                    onChange={(e) => handleLineChange(idx, "user", e.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u._id} value={u._id}>
                        {u.displayName || u.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Comments */}
              <Grid item sx={{ minWidth: 200 }}>
                <TextField
                  label="Comments"
                  fullWidth
                  value={line.comments}
                  onChange={(e) => handleLineChange(idx, "comments", e.target.value)}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item sx={{ minWidth: 80 }}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDuplicateLine(idx)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </Button>
                  {lines.length > 1 && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleRemoveLine(idx)}
                    >
                      <DeleteIcon fontSize="small" />
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Buttons to add more lines */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          mt: 3,
          maxWidth: 1800,
          mx: "auto",
          justifyContent: "flex-start"
        }}
      >
        <Button variant="outlined" onClick={handleAddLine}>
          Add Another Line
        </Button>
        <Button variant="outlined" onClick={handleAddFiveLines}>
          Add 5 Lines
        </Button>
      </Box>

      {/* Save Button */}
      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Container>
  );
}

export default ZReportExpectedPage;
